import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMixpanelContext } from "contexts/MixpanelContext";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { IComposedFilter } from "domain/Utils";
import useArrayState from "hooks/useArrayState";
import { useDateStorageCache } from "hooks/useDateStorageCache";
import { useRouteFilters } from "hooks/useRouteFilters";
import { SBTranslate } from "i18n";
import { DateTime } from "luxon";
import { SBBox, SBGrid, SBIcon, SBLoading, SBOutlinedButton, SBTypography } from "modules/Commons";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import { useNotificationsRequester } from "modules/Notifications/hooks/useNotificationsRequester";
import OnDemandConfirmation from "modules/Purchases/components/OnDemandConfirmation";
import { PurchaseDatePlaceholder } from "modules/Purchases/components/PurchaseDatePlaceholder";
import SBPurchaseTable from "modules/Purchases/components/SBPurchaseTable";
import { IPurchasePlan } from "modules/Purchases/models/IPurchasePlans";
import { usePurchasesRequester } from "modules/Purchases/hooks/usePurchasesRequester";
import { useTranslation } from "react-i18next";
import SBComposedFilter from "views/Commons/SBComposedFilter";
import SBContextDateInfo from "views/Commons/SBContextDateInfo";
import ErrorModal from 'views/PurchaseViews/PurchaseUploaderHelper';

import { IPurchaseCSVErrors } from 'views/PurchaseViews/PurchaseUploaderHelper/IPurchaseCSVErrors';
import { useCSV } from "hooks/useCSV";
import { useSupplyOrderRequester } from "modules/SupplyOrder/hooks/useSupplyOrderRequester";
import SupplyOrderConfirmation from "modules/SupplyOrder/components/SupplyOrderConfirmation";

export default () => {
	const { t } = useTranslation();
	const { error, success } = useSnackbar();
	const tableRef = useRef<any>();
	const { context, filtersContext, setFiltersContextState, buildContextSwitch, notCurrentContext } = useSystemFilterContext();
	const { getDateItem, setDateItem } = useDateStorageCache();
	const { emergencial } = useRouteFilters();
	const { trackExport } = useMixpanelContext();
	const notificationsRequester = useNotificationsRequester();
	const purchasesRequester = usePurchasesRequester();
	const supplyOrderRequester = useSupplyOrderRequester();
	const [loading, setLoading] = useState<boolean>(true);
	const [availableDates, setAvailableDates] = useState<string[]>([]);
	const [dialogStatus, setDialogStatus] = useState<boolean>(false);
	const [filter, setFilter] = useState<IComposedFilter | undefined>(undefined);
	const [loadingExport, setLoadingExport] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [rows, setRows, { hasAny }] = useArrayState<IPurchasePlan>([]);
	const [updateCount, setUpdateCount] = useState(0);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [skip, setSkip] = useState(0);
	const [totalCount, setTotalCount] = useState<number | undefined>();
	const [summaryBase, setSummaryBase] = useState<any>();
	const activeFilterRef = useRef<IComposedFilter | undefined>();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	const [modalErrors, setModalErrors] = useState<IPurchaseCSVErrors[]>([]);
	const { downloadCSV } = useCSV();
	const [csvProcessed, setCsvProcessed] = useState(false);
	const [orderDialogOpen, setOrderDialogOpen] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);

	useEffect(() => {
		if (csvProcessed) {
			setUpdateCount(c => c + 1);
			setCsvProcessed(false);
		}
	}, [csvProcessed]);

	const sendToOrders = () => {
		const selectionState = tableRef.current.selecteds();
		const selectedIndices = Object.keys(selectionState);

		if (selectedIndices.length === 0) {
			error(SBTranslate("noRowsSelectedForOrder"));
			return;
		}

		const plansWithoutOrder = selectedIndices
			.map(i => rows[+i]);

		const orders = plansWithoutOrder.map(plan => {
			const productClientID = plan.inventory.product.clientID;
			const supplierClientID = plan.sourceClientID;
			const quantity = Number(plan.recommended) + Number(plan.modifier);

			return {
				guid: plan.uuid,
				productClientID,
				storeClientID: plan.inventory.store.clientID,
				supplierClientID,
				created: plan.cycle.request,
				expected: plan.cycle.delivery,
				supplyBrainRecommendation: plan.recommended,
				isEmergencial: plan.cycle.isEmergencial,
				quantity,
				cost: quantity * plan.unitCost,
			};
		});

		setOrderLoading(true);
		supplyOrderRequester.createOrders(orders)
			.then((count: number) => {
				const failed = orders.length - count;
				if (failed === 0) {
					success(SBTranslate("ordersCreatedSuccess"));
				} else {
					error(SBTranslate("ordersPartialError", { count: failed }));
				}
				setOrderDialogOpen(false);
				handleApply({ filter: filtersContext?.items } as IComposedFilter);
			})
			.catch(() => {
				error(SBTranslate("ordersCreatedError"));
				setOrderDialogOpen(false);
			})
			.finally(() => setOrderLoading(false));
	};

	const downloadCSVComplete = async () => {
		setLoadingExport(true);
		trackExport({ date: getDateItem() });
		
		let itemsToExport = rows;
		if (totalCount && rows.length < totalCount) {
			itemsToExport = await purchasesRequester.fetchComposedFilteredRecomendation(activeFilterRef.current!, 0, totalCount);
		}

		const csv = tableRef.current.exportCSV(itemsToExport, []);
		const fileName = `${t("suggestedPurchases")}_${DateTime.now().toISODate()}`;
		downloadCSV({ csv, title: fileName });
		
		setLoadingExport(false);
	};

	const downloadTemplateModifiersCSV = async () => {
		setLoadingExport(true);
		
		let itemsToExport = rows;
		if (totalCount && rows.length < totalCount) {
			itemsToExport = await purchasesRequester.fetchComposedFilteredRecomendation(activeFilterRef.current!, 0, totalCount);
		}

		let csvHeaders = [
			SBTranslate("productCode"),
			SBTranslate("storeCode"),
			SBTranslate("modifier")
		];

		const csvHeaderRow = csvHeaders.join(SBTranslate("csvSeparator")) + "\n";
		const csvRows = itemsToExport.map(row => {
			return [
				row.inventory.product.clientID,
				row.inventory.store.clientID,
				row.modifier
			].join(SBTranslate("csvSeparator"));
		}).join("\n");

		const fullCsv = csvHeaderRow + csvRows;

		downloadCSV({ csv: fullCsv, title: SBTranslate("exportTemplate") });
		setLoadingExport(false);
	};

	const getAvailableDates = async () => {
		const result = await notificationsRequester.homeStatisticsDates();
		if (result) setAvailableDates(result)
	};

	const fetchItems = (currentSkip = 0, append = false) => {
		if (append) setIsFetchingMore(true);
		else setLoading(true);

		purchasesRequester
			.fetchComposedFilteredRecomendation(activeFilterRef.current!, currentSkip, 30)
			.then((result) => {
				const updatedRows = result.map((item: any) => ({
					...item,
					modifier: 0
				}));
				setRows((prev) => append ? [...prev, ...updatedRows] : updatedRows);
				setSkip(currentSkip + result.length);
				setHasMore(result.length === 30);

				if (!append && result.length === 0) {
					error(SBTranslate("noItemsFound"));
				}
			})
			.catch(() => error(t("invalidDate")))
			.finally(() => {
				setLoading(false);
				setIsFetchingMore(false);
			});
	};

	const fetchCount = (filter?: IComposedFilter) => {
		purchasesRequester.fetchComposedFilteredRecomendationCount(filter!).then((res) => {
			if (res) {
				setTotalCount(res.totalCount);
				setSummaryBase(res);
			} else {
				setTotalCount(0);
				setSummaryBase(undefined);
			}
		});
	};

	const handleApply = (obj: IComposedFilter) => {
		obj.picker?.date && setDateItem(obj?.picker?.date?.toFormat("yyyy-MM-dd"))
		if (obj.filter)
			setFiltersContextState(obj.filter);
		else
			setFiltersContextState([]);

		activeFilterRef.current = obj;

		if (!buildContextSwitch) {
			fetchItems(0);
			fetchCount(obj);
		}
	};

	const handleOnDemandApply = (filter: IComposedFilter | undefined) => {
		purchasesRequester.runOnDemand(filter ?? {} as IComposedFilter);
	};

	const emergecialFlag = useMemo(() => emergencial(), []);

	useEffect(() => {
		if (context)
			handleApply({ filter: filtersContext?.items } as IComposedFilter);
	}, [context]);

	useEffect(() => {
		getAvailableDates();
	}, []);

	useEffect(() => {
		buildContextSwitch && setLoading(true);
	}, [buildContextSwitch]);

	const handleReUpload = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const SCROLL_THRESHOLD = 600;
	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		if (
			!isFetchingMore &&
			hasMore &&
			scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD
		) {
			fetchItems(skip, true);
		}
	};

	return (
		<SBBox height={1} pl={11}>
			<SBBox>
				<SBComposedFilter.Base onApply={handleApply}>
					<SBComposedFilter.Picker date={getDateItem() && DateTime.fromISO(getDateItem())} range={false} showConfigOptions={false} showDateGranularityOptions={false} availableDates={availableDates} />
					<SBComposedFilter.Filter />
					<SBComposedFilter.Applicator
						applicatorActions={[
							{
								title: SBTranslate("OnDemandRecommendationTitle"),
								onClick: (c) => {
									setDialogStatus(true);
									setFilter(c);
								}
							},
						]}
					/>
					<SBComposedFilter.Actions
						disabled={!hasAny}
						loading={loadingExport}
						actions={[
							{
								icon: { name: "upload" },
								title: SBTranslate("importModifiers"),
								onClick: () => {
									setIsErrorModalOpen(true);
								},
							},
							{
								icon: { name: "download" },
								title: SBTranslate("exportTemplate"),
								onClick: downloadTemplateModifiersCSV,
							},
						]}
					/>
					<SBComposedFilter.Actions
						disabled={!hasAny}
						loading={loadingExport}
						actions={[
							{
								icon: { name: "shopping-cart" },
								title: SBTranslate("createOrders"),
								onClick: () => setOrderDialogOpen(true),
							},
							{
								icon: { name: "download" },
								title: SBTranslate("export"),
								onClick: downloadCSVComplete,
							},
						]}
					/>
					<SBBox mx={1}>
						<SBOutlinedButton disabled fullWidth>
							<SBIcon icon="sbrain" color="gray4" size={17} />
							<SBTypography mx={1} py={0.3} mr="auto" fontWeight="700" textTransform="none">
								{SBTranslate("sendToSupplyBrain")}
							</SBTypography>
						</SBOutlinedButton>
					</SBBox>
				</SBComposedFilter.Base>
				<ErrorModal
					open={isErrorModalOpen}
					errors={modalErrors}
					handleClose={() => setIsErrorModalOpen(false)}
					handleReUpload={handleReUpload}
					rows={rows}
					setRows={setRows}
					setCsvProcessed={setCsvProcessed}
					setModalErrors={setModalErrors}
				/>
			</SBBox>

			{notCurrentContext() ? (
				<SBGrid container>
					<SBContextDateInfo context={DateTime.fromISO(getDateItem())} />
				</SBGrid>
			) : null}

			<SBLoading loading={loading}>
				<SBEmptyState
					show={hasAny}
					emptyState={<PurchaseDatePlaceholder />}
				>
					<SBPurchaseTable
						ref={tableRef}
						title="purchaseRecommendation"
						rows={rows}
						count={totalCount}
						summaryBase={summaryBase}
						emergencialStatus={filtersContext?.items
							?.filter((c) => c.type == "emergencial")
							?.map((c) => String(c.id) === "true")[0]}
						onDataUpdated={setRows}
						onScroll={handleScroll}
						containerHeight={notCurrentContext() ? 365 : undefined}
					/>
				</SBEmptyState>
			</SBLoading>
			<OnDemandConfirmation
				open={dialogStatus}
				onConfirm={() => handleOnDemandApply(filter)}
				onClose={() => setDialogStatus(false)}
				onCancel={() => setDialogStatus(false)}
			/>
			<SupplyOrderConfirmation
				open={orderDialogOpen}
				loading={orderLoading}
				onConfirm={sendToOrders}
				onClose={() => setOrderDialogOpen(false)}
				onCancel={() => setOrderDialogOpen(false)}
			/>
		</SBBox>
	);
};
